import type { LiveUpdateTokenResponse } from "@/logic/api";

const TOKEN_RENEWAL_SKEW_MS = 30_000;

interface TokenClaims {
  exp?: unknown;
}

interface LiveUpdateTokenOptions {
  requestToken: (signal: AbortSignal) => Promise<LiveUpdateTokenResponse>;
  now?: () => number;
  renewalSkewMs?: number;
  onRenewalDue: () => void;
}

export interface LiveUpdateTokenSource {
  getToken(forceRefresh?: boolean): Promise<LiveUpdateTokenResponse>;
  getExpiresAt(): number | null; // For diagnostics
  clear(): void;
  dispose(): void;
}

export class LiveUpdateTokenConfigurationError extends Error {
  constructor() {
    super("Live-update token has no usable expiry");
    this.name = "LiveUpdateTokenConfigurationError";
  }
}

const readExpiry = (token: string): number | null => {
  try {
    const encoded = token.split(".")[1];
    if (!encoded) return null;
    const base64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
    const padding = "=".repeat((4 - (base64.length % 4)) % 4);
    const claims = JSON.parse(atob(`${base64}${padding}`)) as TokenClaims;
    return typeof claims.exp === "number" && Number.isFinite(claims.exp)
      ? claims.exp * 1_000
      : null;
  } catch {
    return null;
  }
};

// Caches one JWT, collapses concurrent requests, and announces renewal before expiry
export const createLiveUpdateTokenSource = ({
  requestToken,
  now = Date.now,
  renewalSkewMs = TOKEN_RENEWAL_SKEW_MS,
  onRenewalDue,
}: LiveUpdateTokenOptions): LiveUpdateTokenSource => {
  // Keep routing and credentials from the same response through retries/renewal
  let cached: { response: LiveUpdateTokenResponse; expiresAt: number } | null = null;
  let inFlight: Promise<LiveUpdateTokenResponse> | null = null;
  let controller: AbortController | null = null;
  let renewalTimer: ReturnType<typeof setTimeout> | null = null;
  let disposed = false;

  const clearRenewal = (): void => {
    if (renewalTimer) clearTimeout(renewalTimer);
    renewalTimer = null;
  };

  const clear = (): void => {
    cached = null;
    clearRenewal();
    // rpc-sync calls may ignore cancellation and their result must still be prevented from
    // restoring the cache or renewal timer after ownership has ended
    controller?.abort();
    controller = null;
    inFlight = null;
  };

  const scheduleRenewal = (expiresAt: number): void => {
    clearRenewal();
    const delay = Math.max(0, expiresAt - renewalSkewMs - now());
    renewalTimer = setTimeout(() => {
      renewalTimer = null;
      cached = null;
      if (!disposed) onRenewalDue();
    }, delay);
  };

  return {
    getToken: (forceRefresh = false) => {
      if (disposed)
        return Promise.reject(new Error("Token source is disposed"));
      if (!forceRefresh && cached && cached.expiresAt - renewalSkewMs > now()) {
        return Promise.resolve(cached.response);
      }
      if (inFlight) return inFlight;

      if (forceRefresh) clear();
      const requestController = new AbortController();
      controller = requestController;
      inFlight = requestToken(requestController.signal)
        .then(({ token, socketUrl }) => {
          if (disposed) throw new Error("Token source is disposed");
          if (requestController.signal.aborted) {
            throw new DOMException("Request aborted", "AbortError");
          }
          const trimmedToken = token.trim();
          const expiresAt = readExpiry(trimmedToken);
          // A token already inside the renewal window would cause an immediate
          // request/reconnect loop instead of establishing a usable connection.
          if (
            !trimmedToken ||
            expiresAt === null ||
            expiresAt <= now() + renewalSkewMs
          ) {
            throw new LiveUpdateTokenConfigurationError();
          }
          const response = {
            token: trimmedToken,
            socketUrl,
          };
          cached = { response, expiresAt };
          scheduleRenewal(expiresAt);
          return response;
        })
        .finally(() => {
          // An obsolete request must not clear a newer request's deduplicationn
          if (controller === requestController) {
            inFlight = null;
            controller = null;
          }
        });

      return inFlight;
    },
    getExpiresAt: () => cached?.expiresAt ?? null,
    clear,
    dispose: () => {
      disposed = true;
      clear();
    },
  };
};
